import {
  axi,
  getBackendAssetPath,
  getBackendAssetUrl,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {createTranslationDict} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {getLocalizedText, validateData} from '@shared/utils';
import {
  GeoFilter,
  GeoFilterOption,
  MarketplaceItemMapping,
  SortOption,
  UploadedFile,
} from '../types';
import {
  adminGeoFilterDetailV8nSchema,
  fileUploadResponseV8nSchema,
  v8nSchemaOfGetGeoFilterOptionsResponse,
  v8nSchemaOfGetMarketplaceItemMappingsResponse,
  v8nSchemaOfGetSortOptionsResponse,
} from './backendSchemas';
import {
  AdminGeoFilterDetailOnBackend,
  FileUploadResponseOnBackend,
  GetGeoFilterOptionsResponse,
  GetMarketplaceItemMappingsResponse,
  GetSortOptionsResponse,
  MarketplaceItemMappingOnBackend,
} from './backendTypes';

export const getSortOptions = async (): Promise<SortOption[]> => {
  const response = await axi.get<GetSortOptionsResponse>('/sorting');

  validateData(
    response.data,
    v8nSchemaOfGetSortOptionsResponse,
    'getSortOptions',
  );

  return response.data ?? [];
};

export const getGeoFilterOptions = async (): Promise<GeoFilterOption[]> => {
  const response =
    await axi.get<GetGeoFilterOptionsResponse>('/admin/geo-filters');

  validateData(
    response.data,
    v8nSchemaOfGetGeoFilterOptionsResponse,
    'getGeoFilterOptions',
  );

  return response.data ?? [];
};

const mapGeoFilterFromBackend = ({
  id,
  name,
  logo_url,
}: AdminGeoFilterDetailOnBackend): GeoFilter => ({
  id,
  name: getLocalizedText(name),
  nameTranslations: createTranslationDict(name),
  logoUrl: getBackendAssetUrl(logo_url ?? ''),
});

export const getGeoFilter = async (slug: string): Promise<GeoFilter> => {
  const response = await axi.get<AdminGeoFilterDetailOnBackend>(
    `/admin/geo-filters/${slug}`,
  );

  try {
    validateData(response.data, adminGeoFilterDetailV8nSchema, 'getGeoFilter');
  } catch {
    //
  }

  return mapGeoFilterFromBackend(response.data);
};

export interface CreateGeoFilterParams {
  slug: string;
  nameTrans: TranslationDict;
  logoUrl?: string;
}

export interface UpdateGeoFilterParams {
  slug: string;
  nameTrans: TranslationDict;
  logoUrl?: string;
}

const cleanTranslations = (
  trans: TranslationDict,
): Partial<TranslationDict> => {
  const result: Partial<TranslationDict> = {};
  Object.entries(trans).forEach(([lang, val]) => {
    if (typeof val === 'string' && val.trim()) {
      result[lang as keyof TranslationDict] = val;
    }
  });
  return result;
};

export const createGeoFilter = async ({
  slug,
  nameTrans,
  logoUrl,
}: CreateGeoFilterParams): Promise<void> => {
  await axi.post('/admin/geo-filters', {
    slug,
    name: cleanTranslations(nameTrans),
    logo_url: logoUrl ? getBackendAssetPath(logoUrl) : undefined,
  });
};

export const updateGeoFilter = async ({
  slug,
  nameTrans,
  logoUrl,
}: UpdateGeoFilterParams): Promise<void> => {
  await axi.put(`/admin/geo-filters/${slug}`, {
    name: cleanTranslations(nameTrans),
    logo_url: logoUrl === undefined ? undefined : getBackendAssetPath(logoUrl),
  });
};

export type AdminUploadFileType =
  | 'avatar'
  | 'gear'
  | 'icon'
  | 'avatar_gear'
  | 'cfp'
  | 'event_logo'
  | 'topic_logo'
  | 'topic_banner'
  | 'category_logo'
  | 'category_banner';

export interface UploadAdminFileParams {
  file: File;
  type: AdminUploadFileType;
}

export const uploadAdminFile = async ({
  file,
  type,
}: UploadAdminFileParams): Promise<UploadedFile> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axi.post<FileUploadResponseOnBackend>(
    '/admin/files/upload',
    formData,
    {
      params: {
        type,
      },
    },
  );

  validateData(response.data, fileUploadResponseV8nSchema, 'uploadAdminFile');

  return {
    key: response.data.key,
    publicUrl: response.data.public_url,
  };
};

const mapMarketplaceItemMappingFromBackend = ({
  id,
  avatar_item_id,
  gear_item_id,
  image_url,
}: MarketplaceItemMappingOnBackend): MarketplaceItemMapping => {
  return {
    id,
    avatarItemId: avatar_item_id,
    gearItemId: gear_item_id,
    imageUrl: getBackendAssetUrl(image_url),
  };
};

export type GetMarketplaceItemMappingsParams = GetListParams;

export const getMarketplaceItemMappings = async ({
  page,
  pageSize,
}: GetMarketplaceItemMappingsParams = {}): Promise<
  GetListOutput<MarketplaceItemMapping>
> => {
  const response = await axi.get<GetMarketplaceItemMappingsResponse>(
    '/admin/rewards/marketplace/items/mappings',
    {
      params: {
        page,
        per_page: pageSize,
      },
    },
  );

  validateData(
    response.data,
    v8nSchemaOfGetMarketplaceItemMappingsResponse,
    'getMarketplaceItemMappings',
  );

  const {
    total,
    total_pages,
    array,
    page: pageFromBackend,
    per_page: pageSizeFromBackend,
  } = response.data;

  return {
    page: pageFromBackend,
    pageSize: pageSizeFromBackend,
    total,
    totalPages: total_pages,
    items: (array ?? []).map(item =>
      mapMarketplaceItemMappingFromBackend(item),
    ),
  };
};

export interface CreateMarketplaceItemMappingParams {
  avatarItemId: string;
  gearItemId: string;
  imageUrl: string;
}

export const createMarketplaceItemMapping = ({
  avatarItemId,
  gearItemId,
  imageUrl,
}: CreateMarketplaceItemMappingParams): Promise<void> => {
  return axi.post('/admin/rewards/marketplace/items/mappings', {
    avatar_item_id: avatarItemId,
    gear_item_id: gearItemId,
    image_url: getBackendAssetPath(imageUrl),
  });
};
